import { apiClient } from '@/lib/api-client';
import type { CustomerDocType, TaxCategory } from '../documents/documents.api';

export type PaymentMethod =
  | 'CASH'
  | 'CARD'
  | 'YAPE'
  | 'PLIN'
  | 'TRANSFER'
  | 'CHECK'
  | 'CREDIT_BALANCE';

export type PaymentTerm = 'CONTADO' | 'CREDITO';

export type SaleStatus = 'PAID' | 'PARTIAL' | 'PENDING' | 'VOIDED';

export interface SaleLineInput {
  productId?: string | null;
  description: string;
  unitCode?: string;
  quantity: number;
  unitPrice: number;
  taxCategory: TaxCategory;
  icbperBags?: number;
}

export interface PaymentInput {
  method: PaymentMethod;
  amount: number;
  receivedAmount?: number;
  reference?: string;
}

export interface SaleCreateInput {
  lines: SaleLineInput[];
  customer: {
    docType: CustomerDocType;
    docNumber: string;
    name: string;
    address?: string | null;
    email?: string;
  };
  payments: PaymentInput[];
  paymentTerm?: PaymentTerm;
  dueDate?: string; // YYYY-MM-DD
  emitDocument?: boolean;
  documentType?: 'FACTURA' | 'BOLETA';
  seriesCode?: string;
  notes?: string;
}

export interface SaleCreateResult {
  saleId: string;
  documentId: string | null;
  status: SaleStatus;
}

export interface SaleSummary {
  id: string;
  tenantId: string;
  totalAmount: string;
  currency: string;
  status: SaleStatus;
  paymentTerm: PaymentTerm;
  dueDate: string | null;
  createdAt: string;
  payments: Array<{
    id: string;
    method: PaymentMethod;
    amount: string;
    receivedAmount: string | null;
    changeGiven: string | null;
    reference: string | null;
    paidAt: string;
  }>;
  document: {
    id: string;
    fullNumber: string;
    type: string;
    sunatStatus: string;
  } | null;
  receivable: {
    id: string;
    balance: string;
    dueDate: string | null;
  } | null;
}

export async function createSale(input: SaleCreateInput): Promise<SaleCreateResult> {
  const { data } = await apiClient.post<SaleCreateResult>('/sales', input);
  return data;
}

export async function listSales(opts: {
  page?: number;
  pageSize?: number;
  status?: string;
  cashSessionId?: string;
} = {}): Promise<{ items: SaleSummary[]; total: number; page: number; pageSize: number }> {
  const { data } = await apiClient.get('/sales', { params: opts });
  return data;
}

export async function getSale(id: string): Promise<SaleSummary> {
  const { data } = await apiClient.get<SaleSummary>(`/sales/${id}`);
  return data;
}

export async function addPayment(
  saleId: string,
  input: PaymentInput,
): Promise<{ saleId: string; status: SaleStatus; balance: string }> {
  const { data } = await apiClient.post(`/sales/${saleId}/payments`, input);
  return data;
}
